import { SWRFetcher } from '@commenda/utils';
import { useMemo } from 'react';
import useSWR from 'swr';
import type { Country, CorporationType } from '@prisma/client';

// --- Response Types (mirrors backend admin-features.types.ts) ---

export enum ActivePackageCategory {
  COMPLIANCE = 'COMPLIANCE',
  INDIRECT_TAX = 'INDIRECT_TAX',
  BOOKKEEPING = 'BOOKKEEPING',
  TRANSFER_PRICING = 'TRANSFER_PRICING',
  INCORPORATION = 'INCORPORATION',
  INVESTMENT_COMPLIANCE = 'INVESTMENT_COMPLIANCE',
  REGISTERED_AGENT = 'REGISTERED_AGENT',
  OTHER = 'OTHER',
}

export interface IActivePackage {
  category: ActivePackageCategory;
  productIdentifier: string;
}

export interface IAdminCompanyHandler {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface IAdminCorporationRow {
  id: number;
  companyLegalName: string;
  incorporationCountry: Country | null;
  corporationType: CorporationType | null;
  isActive: boolean;
  isIncorporationInProgress: boolean;
  activePackages: IActivePackage[];
}

export interface IAdminCompanyRow {
  id: number;
  displayName: string | null;
  email: string | null;
  corporationCount: number;
  activeCorporationCount: number;
  status: 'active' | 'inactive';
  handler: IAdminCompanyHandler | null;
  activePackages: IActivePackage[];
  corporations?: IAdminCorporationRow[];
}

export interface IAdminAggregations {
  totalCompanies: number;
  activeCompanies: number;
  inactiveCompanies: number;
  totalCorporations: number;
  activeCorporations: number;
  inactiveCorporations: number;
}

export interface IAdminCompanyListMeta {
  filteredTotal: number;
  aggregations: IAdminAggregations;
}

export interface IAdminCompanyListResponse {
  companies: IAdminCompanyRow[];
  nextCursor?: string;
  meta: IAdminCompanyListMeta;
}

// --- Single Company Detail ---

export interface IAdminCompanyDetail {
  id: number;
  displayName: string | null;
  email: string | null;
  status: 'active' | 'inactive';
  handler: IAdminCompanyHandler | null;
  corporations: IAdminCorporationRow[];
  preferredBillingCurrency: string | null;
  /** False means this company's own users get a canned notice instead of an AI answer. */
  isAiEnabled: boolean;
  /** Internal test or QA workspace: hidden from Service Delivery, and temporary Commenda access to it does not expire. */
  isTestAccount: boolean;
}

export interface IAdminCompanyDetailResponse {
  company: IAdminCompanyDetail;
}

// --- Hook Options ---

export interface UseAdminCompaniesOptions {
  limit?: number;
  cursor?: string;
  search?: string;
  status?: 'active' | 'inactive';
  handlerId?: string;
  unassignedHandler?: boolean;
  activeProductCategories?: string[];
  corporationCountries?: Country[];
  corporationTypes?: CorporationType[];
  includeCorporations?: boolean;
  enabled?: boolean;
}

// --- Query String Builder ---

function buildAdminCompaniesQueryString(options?: UseAdminCompaniesOptions): string | null {
  if (!options || options.enabled === false) return null;

  const params = new URLSearchParams();

  if (options.limit) {
    params.set('paginationConfig[limit]', String(options.limit));
  }
  if (options.cursor) {
    params.set('paginationConfig[cursor]', options.cursor);
  }
  if (options.search) {
    params.set('filterConfig[search]', options.search);
  }
  if (options.status) {
    params.set('filterConfig[status]', options.status);
  }
  if (options.handlerId) {
    params.set('filterConfig[handlerId]', options.handlerId);
  }
  if (options.unassignedHandler !== undefined) {
    params.set('filterConfig[unassignedHandler]', String(options.unassignedHandler));
  }
  if (options.activeProductCategories?.length) {
    options.activeProductCategories.forEach((cat) => {
      params.append('filterConfig[activeProductCategories][]', cat);
    });
  }
  if (options.corporationCountries?.length) {
    options.corporationCountries.forEach((country) => {
      params.append('filterConfig[corporationCountries][]', country);
    });
  }
  if (options.corporationTypes?.length) {
    options.corporationTypes.forEach((type) => {
      params.append('filterConfig[corporationTypes][]', type);
    });
  }
  if (options.includeCorporations) {
    params.set('includeConfig[corporations]', 'true');
  }

  const qs = params.toString();
  return qs ? `admin/companies?${qs}` : 'admin/companies';
}

// --- SWR Hook ---

export const useAdminCompanies = (options?: UseAdminCompaniesOptions) => {
  const key = buildAdminCompaniesQueryString(options);

  const { data, error, mutate, isLoading, isValidating } = useSWR<IAdminCompanyListResponse>(
    key,
    key ? SWRFetcher({ method: 'GET' }, undefined) : null
  );

  const companies = useMemo<IAdminCompanyRow[]>(() => {
    if (!data) return [];
    return data.companies ?? [];
  }, [data]);

  return {
    companies,
    nextCursor: data?.nextCursor,
    meta: data?.meta,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
  };
};

// --- Single Company Hook ---

export const useAdminCompany = (companyId: number | null) => {
  const key = companyId ? `admin/companies/${companyId}` : null;

  const { data, error, mutate, isLoading } = useSWR<IAdminCompanyDetailResponse>(
    key,
    key ? SWRFetcher({ method: 'GET' }, undefined) : null
  );

  return {
    company: data?.company ?? null,
    error,
    mutate,
    isLoading: !!isLoading,
  };
};
